/**
 * Datacrate Admin predicate — gates partially-implemented / beta UI so it
 * is only visible to the Datacrate operations team, not to tenant users.
 *
 * A "Datacrate Admin" is EITHER:
 *   1. A platform admin — the token `role` claim is `'admin'` (mirrored into
 *      the store as `userRole`). This is the existing platform-wide admin
 *      concept used by AdminPanel / `requireAdminClaim`.
 *   2. A root-company admin — a member of the Datacrate ROOT company
 *      (`ROOT_COMPANY_ID`) whose `companyRole` is `'admin'`.
 *
 * UI hint only — Firestore rules + server-side `requireAdminClaim` remain the
 * authoritative gate for any privileged action. This predicate exists purely
 * to decide what to *render*; hiding a control is not a security boundary.
 *
 * Prefer the `useIsDatacrateAdmin()` hook inside components so the result
 * re-derives when the store's role/companyId hydrate after auth.
 */
import { useStore } from '../store';

/**
 * The Datacrate ROOT company id. Members of this company with the `admin`
 * companyRole are Datacrate Admins. Kept here as the single source of truth
 * so the predicate and any future server mirror reference the same constant.
 */
export const ROOT_COMPANY_ID = 'comp_1780082428333_ythrei';

export interface DatacrateAdminContext {
  /** Platform role claim, mirrored into the store as `userRole`. */
  userRole?: string | null;
  /** The signed-in user's company id (token claim / profile fallback). */
  companyId?: string | null;
  /** The signed-in user's role within their company. */
  companyRole?: string | null;
}

/**
 * Pure predicate — true iff the given identity context is a Datacrate Admin
 * (platform admin OR root-company admin). Safe to call outside React.
 */
export function isDatacrateAdmin(ctx: DatacrateAdminContext): boolean {
  if (ctx.userRole === 'admin') return true;
  return ctx.companyId === ROOT_COMPANY_ID && ctx.companyRole === 'admin';
}

/**
 * React hook — reads the live store identity and returns whether the current
 * user is a Datacrate Admin. Subscribes to the three scalar roots only
 * (`userRole`, `companyId`, `companyRole`) so it re-renders on auth hydration
 * without churning on unrelated store updates.
 */
export function useIsDatacrateAdmin(): boolean {
  const userRole = useStore((s) => s.userRole);
  const companyId = useStore((s) => s.companyId);
  const companyRole = useStore((s) => s.companyRole);
  return isDatacrateAdmin({ userRole, companyId, companyRole });
}
